import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { createGetLatestUpdateInFolder } from '@/utils/folder.utils';
import { color } from '@/colors';
import { FlexCenVer } from '@/UI/styles';
import { useDragAndDrop } from '@/hooks/useDragAndDrop';
import { useUserStore } from '@/state/user.state';
import { I18N_KEYS } from '@/language/keys';
import { goToRecipe } from '@/utils/recipes';
import { EnhancedTableHead } from './components/EnhancedTableHead';
import { FolderThumbnail } from './components/FolderThumbnail';
import { EditableListItem } from './components/EditableListItem';
import {
  descendingComparator,
  calculateFolderRecipeData,
  getListItemDateSortValue,
  type ListItem,
} from './utils/listView.utils';
import type { DashboardRecipe } from '@/types/recipes';
import type { FolderResponseDto } from '@/types/folder.types';
import type { Order, AllowedRecipeKeys } from './types/listView.types';
import type { DashboardItem, EditingNameState } from './DashboardMain';

interface DashboardListViewProps {
  recipes: (DashboardRecipe & { folderId?: string | null; ownerName?: string; owner?: string })[];
  allRecipes?: (DashboardRecipe & { folderId?: string | null; ownerName?: string; owner?: string })[];
  folders?: FolderResponseDto[];
  allFolders?: FolderResponseDto[];
  recipeEditState: EditingNameState;
  handleRenameRecipe: (id: string, name: string) => void;
  handleContextMenu: (event: React.MouseEvent<HTMLDivElement>, id: string, itemType?: DashboardItem) => void;
  cancelRename: () => void;
  onFolderNavigate?: (folderId?: string | null) => void;
  currentFolderId?: string | null;
  folderEditState: EditingNameState;
  handleRenameFolder?: (id: string, name: string) => void;
  cancelRenameFolder?: (id: string) => void;
  countFilesInFolder?: (folderId: string) => number;
  isSearching?: boolean;
  onMoveItem?: (itemId: string, itemType: DashboardItem, targetFolderId: string) => void;
  isWorkspaceFiles?: boolean;
}

function DashboardListView({
  recipes,
  allRecipes,
  folders = [],
  allFolders = [],
  recipeEditState,
  handleRenameRecipe,
  cancelRename,
  handleContextMenu,
  onFolderNavigate,
  currentFolderId,
  folderEditState,
  handleRenameFolder,
  cancelRenameFolder,
  countFilesInFolder,
  isSearching = false,
  onMoveItem,
  isWorkspaceFiles = false,
}: DashboardListViewProps) {
  const foldersForRecursion = allFolders || folders;
  const recipesForLatestUpdate = allRecipes || recipes;

  const getSubtreeUpdatedMs = useMemo(
    () => createGetLatestUpdateInFolder(foldersForRecursion, recipesForLatestUpdate),
    [foldersForRecursion, recipesForLatestUpdate],
  );

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<AllowedRecipeKeys>('updatedAt');
  const [page, setPage] = useState(0);
  const [dense] = useState(false);
  const [rowsPerPage] = useState(5);
  const currentUser = useUserStore((state) => state.user);
  const { t } = useTranslation();
  const { dragState, handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop } = useDragAndDrop();
  const [editingValue, setEditingValue] = useState('');
  const prevEditingIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentId =
      folderEditState.kind === 'editing'
        ? folderEditState.id
        : recipeEditState.kind === 'editing'
          ? recipeEditState.id
          : null;

    if (currentId !== prevEditingIdRef.current) {
      prevEditingIdRef.current = currentId;
      if (folderEditState.kind === 'editing') {
        const folder = folders?.find((f) => f.id === folderEditState.id);
        if (folder) {
          setEditingValue(folder.name);
        }
      } else if (recipeEditState.kind === 'editing') {
        const recipe = recipes?.find((r) => r.id === recipeEditState.id);
        if (recipe) {
          setEditingValue(recipe.name);
        }
      }
    }
  }, [folderEditState, recipeEditState, folders, recipes]);

  const folderWorkflowData = useMemo(
    () => calculateFolderRecipeData(folders, recipes, allRecipes, allFolders),
    [folders, recipes, allRecipes, allFolders],
  );

  const handleRequestSort = (_e: React.MouseEvent<unknown>, property: AllowedRecipeKeys) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (evt: React.MouseEvent<HTMLDivElement>, id: string) => {
    if (evt.detail == 1) {
      goToRecipe(id);
    }
  };

  const handleChangePage = (_e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage);

  const visibleFolders = folders;
  const visibleRecipes = isSearching
    ? recipes // When searching, show all recipes regardless of folder
    : currentFolderId
      ? recipes.filter((recipe) => recipe.folderId === currentFolderId)
      : recipes.filter((recipe) => !recipe.folderId);

  const combinedItems: ListItem[] = useMemo(() => {
    const folderItems = visibleFolders.map((folder) => ({ ...folder, type: 'folder' as const }));
    const recipeItems = visibleRecipes.map((recipe) => ({ ...recipe, type: 'recipe' as const }));
    return [...folderItems, ...recipeItems];
  }, [visibleFolders, visibleRecipes]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - combinedItems.length) : 0;

  const visibleRows = useMemo(() => {
    const sorted = [...combinedItems].sort((a, b) => {
      if (orderBy === 'updatedAt' || orderBy === 'createdAt') {
        const compareResult =
          getListItemDateSortValue(b, orderBy, getSubtreeUpdatedMs) -
          getListItemDateSortValue(a, orderBy, getSubtreeUpdatedMs);
        return order === 'desc' ? compareResult : -compareResult;
      } else if (orderBy === 'files') {
        const filesA = a.type === 'folder' ? folderWorkflowData[a.id]?.count || 0 : 0;
        const filesB = b.type === 'folder' ? folderWorkflowData[b.id]?.count || 0 : 0;

        const compareResult = filesB - filesA;
        return order === 'desc' ? compareResult : -compareResult;
      } else if (orderBy === 'ownerName') {
        let ownerA = '';
        let ownerB = '';

        if (a.type === 'recipe') {
          ownerA = currentUser && a.owner && a.owner === currentUser.uid ? t(I18N_KEYS.GENERAL.YOU) : a.ownerName || '';
        }
        if (b.type === 'recipe') {
          ownerB = currentUser && b.owner && b.owner === currentUser.uid ? t(I18N_KEYS.GENERAL.YOU) : b.ownerName || '';
        }

        const compareResult = ownerB.toLowerCase().localeCompare(ownerA.toLowerCase());
        return order === 'desc' ? compareResult : -compareResult;
      } else {
        if (a.type === 'folder' && b.type === 'recipe') {
          return -1;
        }
        if (a.type === 'recipe' && b.type === 'folder') {
          return 1;
        }

        const compareResult = descendingComparator(a, b, orderBy as keyof ListItem);
        return order === 'desc' ? compareResult : -compareResult;
      }
    });

    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [combinedItems, order, orderBy, page, rowsPerPage, folderWorkflowData, currentUser, t, getSubtreeUpdatedMs]);

  const handleCancelRenameFolder = (id: string) => {
    if (cancelRenameFolder) {
      cancelRenameFolder(id);
    }
  };

  if (!recipes.length && !folders.length) {
    return null;
  }

  return (
    <Box data-testid="dashboard-table-view-container">
      <TableContainer>
        <Table
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
            },
          }}
        >
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            isWorkspaceFiles={isWorkspaceFiles}
          />
          <TableBody>
            {visibleRows.map((item, index) => {
              const labelId = `enhanced-table-checkbox-${index}`;
              const isFolder = item.type === 'folder';
              const isEditingFolder = folderEditState.kind === 'editing';
              const isEditingRecipe = recipeEditState.kind === 'editing';
              const isEditingThis = isFolder
                ? isEditingFolder && folderEditState.id === item.id
                : isEditingRecipe && recipeEditState.id === item.id;
              const canInteract = isFolder ? !isEditingFolder : !isEditingRecipe;

              const updatedDisplayMs = getListItemDateSortValue(item, 'updatedAt', getSubtreeUpdatedMs);
              const createdDisplayMs = getListItemDateSortValue(item, 'createdAt', getSubtreeUpdatedMs);

              return (
                <TableRow
                  hover
                  onClick={(event) => {
                    if (!canInteract) {
                      return;
                    }
                    if (isFolder && onFolderNavigate) {
                      onFolderNavigate(item.id);
                    } else if (!isFolder) {
                      handleClick(event, item.id);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleContextMenu(e, item.id, isFolder ? 'folder' : 'workflow');
                  }}
                  role="checkbox"
                  tabIndex={-1}
                  key={item.id}
                  sx={{
                    cursor: (isFolder ? isEditingThis : !canInteract) ? 'default' : 'grab',
                    border: 'none',
                    opacity: dragState.draggedItemId === item.id ? 0.5 : 1,
                    backgroundColor:
                      isFolder && dragState.dragOverItemId === item.id && dragState.draggedItemId !== item.id
                        ? color.Black88
                        : 'transparent',
                    transition: 'opacity 0.2s, background-color 0.2s',
                  }}
                  draggable={canInteract}
                  onDragStart={(e) => {
                    if (canInteract) {
                      handleDragStart(e, item.id, isFolder ? 'folder' : 'workflow');
                    }
                  }}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => {
                    if (isFolder) {
                      e.preventDefault();
                      handleDragOver(e, item.id);
                    }
                  }}
                  onDragLeave={() => isFolder && handleDragLeave()}
                  onDrop={(e) => {
                    if (isFolder) {
                      e.preventDefault();
                      handleDrop(e, item.id, (itemId, itemType, targetFolderId) => {
                        if (itemId !== targetFolderId) {
                          onMoveItem?.(itemId, itemType, targetFolderId);
                        }
                      });
                    }
                  }}
                >
                  <TableCell
                    component="th"
                    id={labelId}
                    scope="row"
                    padding="none"
                    sx={{ p: 0, borderRadius: `8px 0 0 8px` }}
                  >
                    <FlexCenVer sx={{ p: 1 }}>
                      <Box
                        sx={{
                          width: '144px',
                          height: '72px',
                          borderRadius: 2,
                          position: 'relative',
                          border: `1px solid ${color.White08_T}`,
                          overflow: 'hidden',
                        }}
                      >
                        {isFolder ? (
                          <FolderThumbnail
                            posters={folderWorkflowData[item.id]?.posters ?? []}
                            count={folderWorkflowData[item.id]?.count ?? 0}
                          />
                        ) : (
                          <img
                            style={{
                              display: 'block',
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                            src={item.poster || '/workflow-default-cover-list.png'}
                          />
                        )}
                      </Box>
                      <Box data-testid="dashboard-list-view-recipe-name-container" sx={{ ml: 3 }}>
                        {isFolder && isEditingFolder && folderEditState.id === item.id ? (
                          <EditableListItem
                            isEditing={true}
                            value={editingValue}
                            onChange={setEditingValue}
                            onConfirm={() => {
                              if (isEditingFolder && handleRenameFolder) {
                                handleRenameFolder(folderEditState.id, editingValue);
                              }
                            }}
                            onCancel={() => {
                              if (isEditingFolder) {
                                handleCancelRenameFolder(folderEditState.id);
                              }
                            }}
                            displayValue={item.name}
                          />
                        ) : !isFolder && isEditingRecipe && recipeEditState.id === item.id ? (
                          <EditableListItem
                            isEditing={true}
                            value={editingValue}
                            onChange={setEditingValue}
                            onConfirm={() => {
                              if (isEditingRecipe) {
                                handleRenameRecipe(recipeEditState.id, editingValue);
                              }
                            }}
                            onCancel={() => {
                              if (isEditingRecipe) {
                                cancelRename();
                              }
                            }}
                            displayValue={item.name}
                          />
                        ) : (
                          <EditableListItem isEditing={false} displayValue={item.name} />
                        )}
                      </Box>
                    </FlexCenVer>
                  </TableCell>
                  {isWorkspaceFiles && (
                    <TableCell align="center">
                      <Typography variant="body-sm-rg">
                        {!isFolder && item.type === 'recipe'
                          ? currentUser && item.owner && item.owner === currentUser.uid
                            ? t(I18N_KEYS.GENERAL.YOU)
                            : item.ownerName || '-'
                          : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Typography variant="body-sm-rg">
                      {isFolder
                        ? `${
                            isSearching && countFilesInFolder
                              ? countFilesInFolder(item.id)
                              : folderWorkflowData[item.id]?.count || 0
                          } ${
                            (isSearching && countFilesInFolder
                              ? countFilesInFolder(item.id)
                              : folderWorkflowData[item.id]?.count || 0) === 1
                              ? t(I18N_KEYS.GENERAL.FILE)
                              : t(I18N_KEYS.GENERAL.FILES)
                          }`
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body-sm-rg">
                      {updatedDisplayMs ? getTimeDiffInWords(updatedDisplayMs) : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ borderRadius: '0 8px 8px 0' }}>
                    <Typography variant="body-sm-rg">
                      {createdDisplayMs ? getTimeDiffInWords(createdDisplayMs) : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              >
                <TableCell colSpan={7} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={combinedItems.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
      />
    </Box>
  );
}

export default DashboardListView;
